import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@jotpsych/ui/mui';

import type { CorrectionPreparationResponse, CreateCorrectionRequest } from '../../types';

export interface CorrectionPreparationDialogViewProps {
  preview?: CorrectionPreparationResponse;
  loading: boolean;
  loadFailed: boolean;
  creating: boolean;
  error: string | null;
  choice: string;
  reference: string;
  source: CreateCorrectionRequest['reference_source'];
  note: string;
  confirmed: boolean;
  canConfirm: boolean;
  needsReview: boolean;
  onChoice: (value: string) => void;
  onReference: (value: string) => void;
  onSource: (value: CreateCorrectionRequest['reference_source']) => void;
  onNote: (value: string) => void;
  onConfirmed: (value: boolean) => void;
  onClose: () => void;
  onRetry: () => void;
  onConfirm: () => void;
}

export function CorrectionPreparationDialogView(props: CorrectionPreparationDialogViewProps) {
  const {
    preview,
    loading,
    loadFailed,
    creating,
    error,
    choice,
    reference,
    source,
    note,
    confirmed,
    canConfirm,
    needsReview,
  } = props;
  const existing = preview?.status === 'existing_draft';
  const unavailable = preview?.status === 'unavailable';
  return (
    <Dialog
      open
      onClick={event => event.stopPropagation()}
      onClose={creating ? undefined : props.onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="correction-preparation-title"
    >
      <DialogTitle id="correction-preparation-title">Prepare correction</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {loading && (
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <CircularProgress size={20} />
              <Typography>Checking claim evidence…</Typography>
            </Stack>
          )}
          {loadFailed && (
            <Alert severity="error" action={<Button onClick={props.onRetry}>Retry</Button>}>
              Could not load correction evidence.
            </Alert>
          )}
          {!loading && !loadFailed && preview && (
            <>
              <Stack spacing={0.5}>
                <Typography variant="overline">Submitted payer</Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {preview.payer_name || 'Payer not identified'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The correction keeps this payer and opens as a draft for review.
                </Typography>
              </Stack>
              {existing ? (
                <Alert severity="info">
                  A correction draft already exists. Open it to continue.
                  {needsReview && (
                    <>
                      {' '}
                      Verify its payer reference: <strong>{preview.resolved_reference}</strong>.
                    </>
                  )}
                </Alert>
              ) : unavailable ? (
                <Alert severity="warning">{preview.reason}</Alert>
              ) : (
                <>
                  {preview.status === 'ready' && (
                    <Alert severity="success">
                      Payer reference verified: <strong>{preview.resolved_reference}</strong>
                    </Alert>
                  )}
                  {preview.status !== 'ready' && preview.reason && (
                    <Alert severity="warning">{preview.reason}</Alert>
                  )}
                  <TextField
                    select
                    label="Reference evidence"
                    slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                    value={choice}
                    onChange={event => props.onChoice(event.target.value)}
                    fullWidth
                    disabled={creating}
                  >
                    <MenuItem value="">
                      {preview.status === 'ready'
                        ? 'Use verified reference'
                        : 'Select reference evidence'}
                    </MenuItem>
                    {preview.stored_reference && (
                      <MenuItem
                        value="stored"
                        disabled={preview.candidates?.some(
                          candidate =>
                            candidate.reference === preview.stored_reference &&
                            candidate.attribution_conflict,
                        )}
                      >
                        Stored reference · {preview.stored_reference}
                      </MenuItem>
                    )}
                    {(preview.candidates ?? []).map(candidate => (
                      <MenuItem
                        key={candidate.remittance_claim_id}
                        value={candidate.remittance_claim_id}
                        disabled={candidate.eligibility === 'not_applicable'}
                      >
                        {candidate.reference} · {candidate.payer_name || 'Unidentified payer'} ·{' '}
                        {candidate.payment_date || 'Date unavailable'}
                        {candidate.eligibility === 'not_applicable' ? ' · Cannot use' : ''}
                      </MenuItem>
                    ))}
                    <MenuItem value="manual">Enter a documented reference</MenuItem>
                  </TextField>
                  {(preview.candidates ?? [])
                    .filter(
                      candidate =>
                        candidate.remittance_claim_id === choice ||
                        candidate.eligibility === 'not_applicable',
                    )
                    .map(
                      candidate =>
                        candidate.reason && (
                          <Alert key={candidate.remittance_claim_id} severity="info">
                            {candidate.reference}: {candidate.reason}
                          </Alert>
                        ),
                    )}
                  {choice === 'manual' && (
                    <>
                      <TextField
                        label="Payer claim reference"
                        value={reference}
                        onChange={event => props.onReference(event.target.value)}
                        fullWidth
                        disabled={creating}
                        slotProps={{ htmlInput: { maxLength: 80 } }}
                      />
                      <TextField
                        select
                        label="Verified with"
                        slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                        value={source ?? ''}
                        onChange={event =>
                          props.onSource(
                            (event.target.value ||
                              undefined) as CreateCorrectionRequest['reference_source'],
                          )
                        }
                        fullWidth
                        disabled={creating}
                      >
                        <MenuItem value="" disabled>
                          Select verification source
                        </MenuItem>
                        <MenuItem value="eob">Explanation of benefits (EOB)</MenuItem>
                        <MenuItem value="payer_portal">Payer portal</MenuItem>
                        <MenuItem value="payer_call">Call with payer</MenuItem>
                      </TextField>
                    </>
                  )}
                </>
              )}
              {existing && needsReview && (
                <TextField
                  select
                  label="Verified with"
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                  value={source ?? ''}
                  onChange={event =>
                    props.onSource(
                      (event.target.value ||
                        undefined) as CreateCorrectionRequest['reference_source'],
                    )
                  }
                  fullWidth
                  disabled={creating}
                >
                  <MenuItem value="" disabled>
                    Select verification source
                  </MenuItem>
                  <MenuItem value="eob">Explanation of benefits (EOB)</MenuItem>
                  <MenuItem value="payer_portal">Payer portal</MenuItem>
                  <MenuItem value="payer_call">Call with payer</MenuItem>
                </TextField>
              )}
              {needsReview && (
                <>
                  <TextField
                    label="How did you verify this reference?"
                    helperText="Document the source and how you confirmed it belongs to the submitted payer."
                    value={note}
                    onChange={event => props.onNote(event.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    disabled={creating}
                    slotProps={{ htmlInput: { maxLength: 2000 } }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmed}
                        onChange={event => props.onConfirmed(event.target.checked)}
                        disabled={creating}
                      />
                    }
                    label="I verified this reference belongs to this claim and its submitted payer."
                  />
                </>
              )}
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={props.onConfirm}
          disabled={!canConfirm || creating || loading || loadFailed}
        >
          {creating ? 'Preparing…' : existing ? 'Open correction draft' : 'Create correction draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
